import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  FiPlus,
  FiEdit2,
  FiTrash2,
  FiFolder,
  FiRefreshCw,
  FiSearch,
  FiChevronDown,
  FiChevronRight,
  FiX,
  FiAlertCircle,
  FiDownload,
  FiEye,
  FiLink,
  FiFile,
  FiImage,
  FiVideo,
  FiUpload,
  FiSave,
  FiInfo,
  FiCheckCircle,
  FiMaximize2,
  FiFileText,
  FiTrash,
  FiList,
  FiGrid,
  FiPaperclip,
  FiMoreVertical,
  FiLayers
} from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';
import api from '../../../api';

// ------------------------------------------------------------
// PDF.js loader (CDN only — no npm "pdfjs-dist" dependency)
//
// PDF.js renders pages to a <canvas> using pure JS — it never invokes the
// browser's native PDF plugin, so it behaves identically on desktop, real
// mobile browsers, and Chrome DevTools mobile emulation (unlike an
// <iframe> pointed at a PDF, which hands rendering off to PDFium and can
// conflict with DevTools' device toolbar).
//
// IMPORTANT: the library script and the worker script MUST be the exact
// same version, and that version must be one of the "classic" builds that
// still ships pdf.min.js / pdf.worker.min.js as plain scripts (v4.x and
// earlier). Newer major versions (v5+) ship ES-module-only builds
// (pdf.mjs / pdf.worker.mjs), and pointing GlobalWorkerOptions.workerSrc
// at those from a classic script tag causes the browser to attempt a
// dynamic import() on them, which fails with an error like:
//   "Uncaught TypeError: Failed to fetch dynamically imported module: ...pdf.worker.min.js"
// Pinning both URLs to the SAME known-good legacy version avoids that
// entirely. Do not import pdfjs-dist from npm alongside this — mixing an
// installed package version with a CDN worker version is exactly what
// causes the mismatch.
const PDFJS_VERSION = '3.11.174';
const PDFJS_LIB_URL = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${PDFJS_VERSION}/pdf.min.js`;
const PDFJS_WORKER_URL = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${PDFJS_VERSION}/pdf.worker.min.js`;

let pdfjsLoadPromise = null;
const loadPdfJs = () => {
  if (window.pdfjsLib) return Promise.resolve(window.pdfjsLib);
  if (pdfjsLoadPromise) return pdfjsLoadPromise;

  pdfjsLoadPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = PDFJS_LIB_URL;
    script.async = true;
    script.onload = () => {
      window.pdfjsLib.GlobalWorkerOptions.workerSrc = PDFJS_WORKER_URL;
      resolve(window.pdfjsLib);
    };
    script.onerror = () => {
      pdfjsLoadPromise = null;
      reject(new Error('Could not load the PDF renderer script'));
    };
    document.body.appendChild(script);
  });

  return pdfjsLoadPromise;
};

// ============================================================
// HELPER FUNCTIONS (Global)
// ============================================================

const getTypeIcon = (type) => {
  const icons = {
    pdf: FiFile,
    document: FiFileText,
    presentation: FiFile,
    video: FiVideo,
    audio: FiFile,
    image: FiImage,
    link: FiLink,
    other: FiFile
  };
  return icons[type] || FiFile;
};

const getTypeBadge = (type) => {
  const configs = {
    pdf: { color: 'bg-rose-50 text-rose-700 border-rose-200', label: 'PDF' },
    document: { color: 'bg-blue-50 text-blue-700 border-blue-200', label: 'Document' },
    presentation: { color: 'bg-amber-50 text-amber-700 border-amber-200', label: 'PPT' },
    video: { color: 'bg-purple-50 text-purple-700 border-purple-200', label: 'Video' },
    audio: { color: 'bg-indigo-50 text-indigo-700 border-indigo-200', label: 'Audio' },
    image: { color: 'bg-pink-50 text-pink-700 border-pink-200', label: 'Image' },
    link: { color: 'bg-cyan-50 text-cyan-700 border-cyan-200', label: 'Web Link' },
    other: { color: 'bg-slate-50 text-slate-700 border-slate-200', label: 'Other' }
  };
  return configs[type] || configs.other;
};

const formatFileSize = (bytes) => {
  if (!bytes) return '0 KB';
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / 1048576).toFixed(1) + ' MB';
};

const isPreviewable = (fileType) => {
  const previewableTypes = ['image', 'pdf', 'video', 'document'];
  return previewableTypes.includes(fileType);
};

// Robust CSS-driven variants decoupled from window.innerWidth to prevent viewport calculation jumps
const pureCssVariants = {
  hidden: { 
    opacity: 0,
    transform: 'translateY(var(--modal-initial-y, 100%)) scale(var(--modal-initial-scale, 1))' 
  },
  visible: { 
    opacity: 1,
    transform: 'translateY(0px) scale(1)',
    transition: { type: 'spring', damping: 28, stiffness: 380 }
  },
  exit: { 
    opacity: 0,
    transform: 'translateY(var(--modal-exit-y, 80%)) scale(var(--modal-exit-scale, 0.98))',
    transition: { duration: 0.18, ease: 'easeInOut' }
  }
};

// ============================================================
// MAIN COMPONENT
// ============================================================
const StudyMaterialManager = ({ classItem, subject, labBatch, facultyId, refreshKey, onRefresh }) => {
  // ===== STATE =====
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('list');
  const [expandedUnits, setExpandedUnits] = useState({});
  const [showUnitModal, setShowUnitModal] = useState(false);
  const [showMaterialModal, setShowMaterialModal] = useState(false);
  const [showMaterialDetailModal, setShowMaterialDetailModal] = useState(false);
  const [selectedMaterial, setSelectedMaterial] = useState(null);
  const [selectedUnitId, setSelectedUnitId] = useState(null);
  const [editingUnit, setEditingUnit] = useState(null);
  const [editingMaterial, setEditingMaterial] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [deleteType, setDeleteType] = useState(null);
  const [notification, setNotification] = useState(null);

  const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';

  // ===== NOTIFICATION =====
  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 5000);
  };

  // ===== FETCH UNITS =====
  const fetchUnits = useCallback(async () => {
    if (!classItem?.classId || !subject?.subjectId) return;
    
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (labBatch?.batchId) params.append('labBatchId', labBatch.batchId);
      
      const url = `/api/faculty/lms/study-material/units/${classItem.classId}/${subject.subjectId}?${params}`;
      const response = await api.get(url);
      
      if (response.data?.success) {
        setUnits(response.data.data.units || []);
        const expanded = {};
        (response.data.data.units || []).forEach(unit => {
          expanded[unit._id] = true;
        });
        setExpandedUnits(expanded);
      }
    } catch (error) {
      console.error('Error fetching units:', error);
      showNotification('Failed to load study reference chapters', 'error');
    } finally {
      setLoading(false);
    }
  }, [classItem, subject, labBatch]);

  useEffect(() => {
    fetchUnits();
  }, [fetchUnits, refreshKey]);

  // ===== TOGGLE UNIT =====
  const toggleUnit = (unitId) => {
    setExpandedUnits(prev => ({
      ...prev,
      [unitId]: !prev[unitId]
    }));
  };

  // ===== EXPAND ALL / COLLAPSE ALL =====
  const toggleAllUnits = () => {
    const allExpanded = units.reduce((acc, unit) => {
      acc[unit._id] = !Object.values(expandedUnits).every(v => v === true);
      return acc;
    }, {});
    setExpandedUnits(allExpanded);
  };

  // ===== OPEN MATERIAL DETAIL =====
  const openMaterialDetail = async (material) => {
    try {
      setSelectedMaterial(material);
      setShowMaterialDetailModal(true);
    } catch (error) {
      console.error('Error opening material detail:', error);
      showNotification('Failed to load material details', 'error');
    }
  };

  // ===== CRUD OPERATIONS =====
  const handleCreateUnit = async (unitData) => {
    if (!unitData) {
      showNotification('No chapter information received', 'error');
      return;
    }
    
    try {
      const payload = {
        classId: classItem.classId,
        subjectId: subject.subjectId,
        labBatchId: labBatch?.batchId || null,
        unitNumber: unitData.unitNumber || 1,
        title: unitData.title || '',
        description: unitData.description || '',
        isPublished: unitData.isPublished !== undefined ? unitData.isPublished : true
      };
      
      if (!payload.title) {
        showNotification('Chapter title is required', 'error');
        return;
      }
      
      const response = await api.post('/api/faculty/lms/study-material/unit', payload);
      
      if (response.data?.success) {
        showNotification('Chapter structure created successfully', 'success');
        fetchUnits();
        if (onRefresh) onRefresh();
        setShowUnitModal(false);
      }
    } catch (error) {
      console.error('Error creating unit:', error);
      showNotification(error.response?.data?.message || 'Failed to create unit', 'error');
    }
  };

  const handleUpdateUnit = async (unitId, unitData) => {
    try {
      const payload = {
        unitNumber: unitData.unitNumber,
        title: unitData.title,
        description: unitData.description || '',
        isPublished: unitData.isPublished !== undefined ? unitData.isPublished : true
      };
      
      const response = await api.put(`/api/faculty/lms/study-material/unit/${unitId}`, payload);
      
      if (response.data?.success) {
        showNotification('Chapter structure updated successfully', 'success');
        fetchUnits();
        if (onRefresh) onRefresh();
        setShowUnitModal(false);
        setEditingUnit(null);
      }
    } catch (error) {
      console.error('Error updating unit:', error);
      showNotification(error.response?.data?.message || 'Failed to update unit', 'error');
    }
  };

  const handleDeleteUnit = async (unitId) => {
    try {
      const response = await api.delete(`/api/faculty/lms/study-material/unit/${unitId}`);
      
      if (response.data?.success) {
        showNotification('Chapter and all related resources deleted successfully', 'success');
        fetchUnits();
        if (onRefresh) onRefresh();
        setShowDeleteConfirm(null);
        setDeleteType(null);
      }
    } catch (error) {
      console.error('Error deleting unit:', error);
      showNotification(error.response?.data?.message || 'Failed to delete unit', 'error');
    }
  };

  // ===== MATERIAL CRUD OPERATIONS =====
  const handleAddMaterial = async (unitId, materialData, files) => {
    try {
      const formData = new FormData();
      formData.append('title', materialData.title || '');
      formData.append('description', materialData.description || '');
      formData.append('type', materialData.type || 'other');
      formData.append('externalLink', materialData.externalLink || '');
      formData.append('duration', materialData.duration || '');
      
      if (files && files.length > 0) {
        for (const file of files) {
          formData.append('files', file);
        }
      }
      
      const response = await api.post(
        `/api/faculty/lms/study-material/unit/${unitId}/materials`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      if (response.data?.success) {
        showNotification(response.data.message || 'Reference document added successfully', 'success');
        fetchUnits();
        if (onRefresh) onRefresh();
        setShowMaterialModal(false);
        setSelectedUnitId(null);
      }
    } catch (error) {
      console.error('Error adding material:', error);
      showNotification(error.response?.data?.message || 'Failed to add material', 'error');
    }
  };

  const handleAddFileToMaterial = async (materialId, file) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await api.post(
        `/api/faculty/lms/study-material/material/${materialId}/files`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      if (response.data?.success) {
        showNotification('File attached successfully', 'success');
        fetchUnits();
        if (onRefresh) onRefresh();
        if (showMaterialDetailModal && selectedMaterial) {
          setSelectedMaterial(prev => ({
            ...prev,
            files: [...(prev.files || []), response.data.data]
          }));
        }
      }
    } catch (error) {
      console.error('Error adding file:', error);
      showNotification(error.response?.data?.message || 'Failed to add file', 'error');
    }
  };

  const handleRemoveFileFromMaterial = async (materialId, fileIndex) => {
    try {
      const response = await api.delete(
        `/api/faculty/lms/study-material/material/${materialId}/files/${fileIndex}`
      );
      
      if (response.data?.success) {
        showNotification('File removed from reference item', 'success');
        fetchUnits();
        if (onRefresh) onRefresh();
        if (showMaterialDetailModal && selectedMaterial) {
          const updatedFiles = selectedMaterial.files.filter((_, index) => index !== fileIndex);
          setSelectedMaterial(prev => ({
            ...prev,
            files: updatedFiles
          }));
        }
      }
    } catch (error) {
      console.error('Error removing file:', error);
      showNotification(error.response?.data?.message || 'Failed to remove file', 'error');
    }
  };

  const handleUpdateMaterial = async (materialId, materialData, newFiles) => {
    try {
      const formData = new FormData();
      formData.append('title', materialData.title || '');
      formData.append('description', materialData.description || '');
      formData.append('type', materialData.type || 'other');
      formData.append('externalLink', materialData.externalLink || '');
      formData.append('duration', materialData.duration || '');
      
      if (materialData._action) {
        formData.append('_action', materialData._action);
      }
      
      if (newFiles && newFiles.length > 0) {
        for (const file of newFiles) {
          formData.append('files', file);
        }
      }
      
      const response = await api.put(
        `/api/faculty/lms/study-material/material/${materialId}`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      if (response.data?.success) {
        showNotification(response.data.message || 'Reference content updated', 'success');
        fetchUnits();
        if (onRefresh) onRefresh();
        setShowMaterialModal(false);
        setEditingMaterial(null);
        if (showMaterialDetailModal) {
          setSelectedMaterial(null);
          setShowMaterialDetailModal(false);
        }
      }
    } catch (error) {
      console.error('Error updating material:', error);
      showNotification(error.response?.data?.message || 'Failed to update material', 'error');
    }
  };

  const handleDeleteMaterial = async (materialId) => {
    try {
      const response = await api.delete(`/api/faculty/lms/study-material/material/${materialId}`);
      
      if (response.data?.success) {
        showNotification('Material card removed successfully', 'success');
        fetchUnits();
        if (onRefresh) onRefresh();
        setShowDeleteConfirm(null);
        setDeleteType(null);
        if (showMaterialDetailModal) {
          setSelectedMaterial(null);
          setShowMaterialDetailModal(false);
        }
      }
    } catch (error) {
      console.error('Error deleting material:', error);
      showNotification(error.response?.data?.message || 'Failed to delete material', 'error');
    }
  };

  // ===== MODAL CONTROLS =====
  const openAddUnitModal = () => {
    setEditingUnit(null);
    setShowUnitModal(true);
  };

  const openEditUnitModal = (unit) => {
    setEditingUnit(unit);
    setShowUnitModal(true);
  };

  const openAddMaterialModal = (unitId) => {
    setSelectedUnitId(unitId);
    setEditingMaterial(null);
    setShowMaterialModal(true);
  };

  const openEditMaterialModal = (material) => {
    setEditingMaterial(material);
    const unit = units.find(u => 
      u.materials.some(m => m._id === material._id)
    );
    setSelectedUnitId(unit?._id || null);
    setShowMaterialModal(true);
  };

  // ===== FILTER UNITS =====
  const filteredUnits = units.filter(unit => {
    if (!searchTerm) return true;
    
    const searchLower = searchTerm.toLowerCase();
    if (unit.title?.toLowerCase().includes(searchLower)) return true;
    if (unit.description?.toLowerCase().includes(searchLower)) return true;
    
    return unit.materials?.some(m => 
      m.title?.toLowerCase().includes(searchLower) ||
      m.description?.toLowerCase().includes(searchLower)
    );
  });

  // ===== STATS =====
  const totalMaterials = units.reduce((sum, u) => sum + (u.materials?.length || 0), 0);
  const totalFiles = units.reduce((sum, u) => {
    return sum + u.materials.reduce((s, m) => s + (m.files?.length || 0), 0);
  }, 0);

  return (
    <div className="space-y-5 sm:space-y-6 text-xs">
      {/* Toast Alerts Notification banner */}
      <AnimatePresence>
        {notification && (
          <motion.div
            initial={{ opacity: 0, y: -20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -20, scale: 0.95 }}
            className={`fixed top-4 right-4 z-50 p-4 rounded-xl shadow-lg border flex items-start gap-3 max-w-sm font-semibold backdrop-blur-md ${
              notification.type === 'success' 
                ? 'bg-emerald-50 border-emerald-200 text-emerald-800' 
                : 'bg-rose-50 border-rose-200 text-rose-800'
            }`}
          >
            {notification.type === 'success' 
              ? <FiCheckCircle className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
              : <FiAlertCircle className="w-4 h-4 text-rose-500 mt-0.5 flex-shrink-0" />
            }
            <span className="flex-1 leading-normal text-xs">{notification.message}</span>
            <button onClick={() => setNotification(null)} className="text-slate-400 hover:text-slate-600">
              <FiX className="w-4 h-4" />
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ===== BAR ACTION HEADER ===== */}
      <div className="bg-white rounded-xl border border-slate-200/80 shadow-sm p-4 space-y-4">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 rounded-xl bg-indigo-50 border border-indigo-100 flex items-center justify-center text-indigo-600 flex-shrink-0">
              <FiFolder className="w-5 h-5" />
            </div>
            <div className="min-w-0">
              <h2 className="text-base font-bold text-slate-800">Study Materials Repository</h2>
              <p className="text-xs text-slate-400 font-medium mt-0.5">
                {units.length} Syllabus Chapters · {totalMaterials} Material Items · {totalFiles} Total Files
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-2 flex-wrap w-full sm:w-auto justify-end">
            <div className="flex bg-slate-100 p-0.5 rounded-lg border border-slate-200/40">
              <button
                onClick={() => setViewMode('list')}
                className={`p-1.5 rounded-md transition-all ${
                  viewMode === 'list' ? 'bg-white shadow-sm text-indigo-600' : 'text-slate-400 hover:text-slate-600'
                }`}
                title="List Compact View"
              >
                <FiList className="w-3.5 h-3.5" />
              </button>
              <button
                onClick={() => setViewMode('grid')}
                className={`p-1.5 rounded-md transition-all ${
                  viewMode === 'grid' ? 'bg-white shadow-sm text-indigo-600' : 'text-slate-400 hover:text-slate-600'
                }`}
                title="Grid Matrix View"
              >
                <FiGrid className="w-3.5 h-3.5" />
              </button>
            </div>

            <button
              onClick={fetchUnits}
              className="p-2 border border-slate-200 text-slate-500 hover:text-slate-700 hover:bg-slate-50 rounded-lg transition-all"
              title="Refresh Items"
              disabled={loading}
            >
              <FiRefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
            </button>

            {units.length > 0 && (
              <button
                onClick={toggleAllUnits}
                className="px-3 py-2 text-xs font-semibold text-slate-600 bg-white border border-slate-200 hover:bg-slate-50 rounded-lg transition-all shadow-sm"
              >
                {Object.values(expandedUnits).every(v => v === true) ? 'Collapse All' : 'Expand All'}
              </button>
            )}

            <button
              onClick={openAddUnitModal}
              className="flex items-center gap-1.5 px-3 py-2 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-lg text-xs transition-all shadow-sm shadow-indigo-100"
            >
              <FiPlus className="w-3.5 h-3.5" />
              Add Chapter Structure
            </button>
          </div>
        </div>

        {/* Dynamic Search Bar Field Row */}
        <div className="relative pt-1 border-t border-slate-100">
          <FiSearch className="absolute left-3 top-1/2 text-slate-400 w-4 h-4" />
          <input
            type="text"
            placeholder="Search by chapter title, reference topic name or sub-description content..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-9 pr-8 py-2 bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 text-xs text-slate-700 transition-all placeholder:text-slate-400"
          />
          {searchTerm && (
            <button
              onClick={() => setSearchTerm('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
            >
              <FiX className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>

      {/* ===== CONTENT DISPLAY MATRIX ===== */}
      {loading ? (
        <div className="flex flex-col items-center justify-center py-20 bg-white rounded-xl border border-slate-200/80 shadow-sm space-y-3">
          <div className="animate-spin rounded-full h-7 w-7 border-2 border-indigo-600 border-t-transparent"></div>
          <p className="text-xs text-slate-400 font-medium">Reading materials repository data...</p>
        </div>
      ) : filteredUnits.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-xl border border-slate-200/80 shadow-sm px-4">
          <div className="w-12 h-12 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-3 border border-slate-100 text-slate-400">
            <FiFolder className="w-5 h-5" />
          </div>
          <p className="text-sm font-semibold text-slate-700">
            {searchTerm ? 'No matching records discovered' : 'No reference assets published yet'}
          </p>
          <p className="text-xs text-slate-400 mt-1 max-w-xs mx-auto">
            {searchTerm 
              ? 'Try modifying your keyword text filter rules' 
              : 'Click "Add Chapter Structure" to start planning and partitioning lesson materials.'}
          </p>
        </div>
      ) : (
        <div className={viewMode === 'grid' ? 'grid grid-cols-1 md:grid-cols-2 gap-4' : 'space-y-3'}>
          {filteredUnits.map((unit) => {
            const isExpanded = expandedUnits[unit._id];
            const materialCount = unit.materials?.length || 0;
            
            return (
              <div
                key={unit._id}
                className="bg-white rounded-xl border border-slate-200/80 shadow-sm overflow-hidden hover:border-indigo-200 transition-all flex flex-col"
              >
                {/* Unit Accordion Folder Bar header */}
                <div
                  className="flex items-center justify-between p-3.5 cursor-pointer hover:bg-slate-50/60 transition-colors group select-none"
                  onClick={() => toggleUnit(unit._id)}
                >
                  <div className="flex items-center gap-2.5 min-w-0 flex-1">
                    <div className="text-slate-400 group-hover:text-slate-600 transition-colors flex-shrink-0">
                      {isExpanded ? <FiChevronDown className="w-4 h-4" /> : <FiChevronRight className="w-4 h-4" />}
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-[10px] font-bold text-slate-500 bg-slate-100 border border-slate-200/40 px-1.5 py-0.2 rounded">
                          Chapter {unit.unitNumber}
                        </span>
                        <h3 className="text-xs font-bold text-slate-800 truncate leading-snug">{unit.title}</h3>
                      </div>
                      {unit.description && (
                        <p className="text-[11px] text-slate-400 truncate mt-0.5 font-normal">{unit.description}</p>
                      )}
                    </div>
                  </div>
                  
                  {/* Folder action controls buttons map */}
                  <div className="flex items-center gap-1 flex-shrink-0 ml-2" onClick={(e) => e.stopPropagation()}>
                    <span className="text-[10px] font-bold text-slate-400 bg-slate-50 px-1.5 py-0.5 rounded border border-slate-200/40 mr-1">
                      {materialCount} {materialCount === 1 ? 'asset' : 'assets'}
                    </span>
                    <button
                      onClick={() => openAddMaterialModal(unit._id)}
                      className="w-7 h-7 flex items-center justify-center text-indigo-600 hover:bg-indigo-50 rounded-lg transition-all"
                      title="Attach Item Content"
                    >
                      <FiPlus className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => openEditUnitModal(unit)}
                      className="w-7 h-7 flex items-center justify-center text-blue-600 hover:bg-blue-50 rounded-lg transition-all"
                      title="Edit Structure Info"
                    >
                      <FiEdit2 className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => {
                        setShowDeleteConfirm(unit._id);
                        setDeleteType('unit');
                      }}
                      className="w-7 h-7 flex items-center justify-center text-rose-600 hover:bg-rose-50 rounded-lg transition-all"
                      title="Drop Chapter Folder"
                    >
                      <FiTrash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>

                {/* Materials list items under current folder index */}
                {isExpanded && (
                  <div className="border-t border-slate-100 bg-slate-50/20 flex-1">
                    {materialCount === 0 ? (
                      <div className="p-6 text-center text-slate-400">
                        <FiFile className="w-7 h-7 mx-auto mb-1 text-slate-300" />
                        <p className="text-[11px] font-medium">No files linked to this folder yet.</p>
                        <button
                          onClick={() => openAddMaterialModal(unit._id)}
                          className="text-[11px] text-indigo-600 hover:text-indigo-800 font-bold mt-1.5 block mx-auto underline"
                        >
                          + Upload files now
                        </button>
                      </div>
                    ) : (
                      <div className="divide-y divide-slate-100">
                        {unit.materials?.map((material, index) => {
                          const TypeIcon = getTypeIcon(material.type);
                          const typeConfig = getTypeBadge(material.type);
                          const fileCount = material.files?.length || 0;
                          
                          return (
                            <div
                              key={material._id || index}
                              className="flex items-center justify-between p-3 hover:bg-slate-50/80 transition-colors group/item cursor-pointer"
                              onClick={() => openMaterialDetail(material)}
                            >
                              <div className="flex items-center gap-3 flex-1 min-w-0">
                                <div className={`w-8 h-8 rounded-lg flex items-center justify-center border flex-shrink-0 ${typeConfig.color}`}>
                                  <TypeIcon className="w-3.5 h-3.5" />
                                </div>
                                <div className="min-w-0 flex-1 space-y-0.5">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <span className="text-xs font-bold text-slate-700 group-hover/item:text-indigo-600 transition-colors truncate">
                                      {material.title}
                                    </span>
                                    <span className={`px-1.5 py-0.2 rounded text-[9px] font-bold border uppercase tracking-wide ${typeConfig.color}`}>
                                      {typeConfig.label}
                                    </span>
                                    {fileCount > 0 && (
                                      <span className="text-[10px] text-slate-400 flex items-center gap-0.5 font-medium">
                                        <FiPaperclip className="w-3 h-3 text-slate-300" />
                                        {fileCount} {fileCount === 1 ? 'file' : 'files'}
                                      </span>
                                    )}
                                  </div>
                                  {material.description && (
                                    <p className="text-[11px] text-slate-400 truncate font-normal leading-normal">{material.description}</p>
                                  )}
                                </div>
                              </div>
                              
                              <div className="flex items-center gap-0.5 flex-shrink-0 ml-2" onClick={(e) => e.stopPropagation()}>
                                {material.totalViews > 0 && (
                                  <span className="text-[11px] text-slate-400 flex items-center gap-1 px-1.5 font-medium font-mono">
                                    <FiEye className="w-3 h-3 text-slate-300" />
                                    {material.totalViews}
                                  </span>
                                )}
                                <button
                                  onClick={() => openEditMaterialModal(material)}
                                  className="w-7 h-7 flex items-center justify-center text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                                  title="Edit Material Sheet"
                                >
                                  <FiEdit2 className="w-3 h-3" />
                                </button>
                                <button
                                  onClick={() => {
                                    setShowDeleteConfirm(material._id);
                                    setDeleteType('material');
                                  }}
                                  className="w-7 h-7 flex items-center justify-center text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-md transition-colors"
                                  title="Drop Resource Card"
                                >
                                  <FiTrash2 className="w-3 h-3" />
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ===== RENDERING DETACHED MODAL OVERLAYS ===== */}
      <UnitModal
        isOpen={showUnitModal}
        onClose={() => {
          setShowUnitModal(false);
          setEditingUnit(null);
        }}
        onSave={editingUnit ? handleUpdateUnit : handleCreateUnit}
        editingUnit={editingUnit}
        nextUnitNumber={units.length + 1}
      />

      <MaterialModal
        isOpen={showMaterialModal}
        onClose={() => {
          setShowMaterialModal(false);
          setEditingMaterial(null);
          setSelectedUnitId(null);
        }}
        onSave={editingMaterial ? handleUpdateMaterial : handleAddMaterial}
        editingMaterial={editingMaterial}
        unitId={selectedUnitId}
        units={units}
        apiBaseUrl={API_BASE_URL}
      />

      <MaterialDetailModal
        isOpen={showMaterialDetailModal}
        onClose={() => {
          setShowMaterialDetailModal(false);
          setSelectedMaterial(null);
        }}
        material={selectedMaterial}
        unit={units.find(u => u.materials?.some(m => m._id === selectedMaterial?._id))}
        apiBaseUrl={API_BASE_URL}
        onEdit={() => {
          if (selectedMaterial) {
            setShowMaterialDetailModal(false);
            openEditMaterialModal(selectedMaterial);
          }
        }}
        onDelete={() => {
          if (selectedMaterial) {
            setShowMaterialDetailModal(false);
            setShowDeleteConfirm(selectedMaterial._id);
            setDeleteType('material');
          }
        }}
        onAddFile={handleAddFileToMaterial}
        onRemoveFile={handleRemoveFileFromMaterial}
      />

      {/* Replaced confirm overlay drawer alert element */}
      <AnimatePresence>
        {showDeleteConfirm && (
          <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
            <motion.div
              variants={pureCssVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              className="bg-white rounded-t-2xl sm:rounded-2xl max-w-sm w-full p-5 border border-slate-100 shadow-xl [--modal-initial-y:100%] sm:[--modal-initial-y:20px] sm:[--modal-initial-scale:0.96] [--modal-exit-y:100%] sm:[--modal-exit-y:15px]"
            >
              <div className="w-full flex justify-center py-2 sm:hidden bg-white">
                <div className="w-12 h-1 bg-slate-200 rounded-full" />
              </div>
              <div className="flex items-center gap-3 text-rose-600 mb-3 mt-1 sm:mt-0">
                <div className="w-9 h-9 bg-rose-50 rounded-xl flex items-center justify-center text-rose-500 border border-rose-100">
                  <FiAlertCircle className="w-5 h-5" />
                </div>
                <h3 className="text-sm font-bold text-slate-800">
                  {deleteType === 'unit' ? 'Confirm Chapter Removal' : 'Confirm Material Removal'}
                </h3>
              </div>
              <p className="text-xs text-slate-500 leading-normal mb-5">
                {deleteType === 'unit' 
                  ? 'Are you sure you want to drop this complete chapter folder structure including ALL attachments? This action cannot be reversed.'
                  : 'Are you sure you want to delete this specific reference asset and its linked file documents? This action cannot be undone.'
                }
              </p>
              <div className="flex gap-2.5 justify-stretch sm:justify-end">
                <button
                  type="button"
                  onClick={() => {
                    setShowDeleteConfirm(null);
                    setDeleteType(null);
                  }}
                  className="flex-1 sm:flex-none px-3.5 py-2 text-slate-600 border border-slate-200 bg-white hover:bg-slate-50 text-xs font-semibold rounded-lg shadow-sm transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => {
                    if (deleteType === 'unit') {
                      handleDeleteUnit(showDeleteConfirm);
                    } else {
                      handleDeleteMaterial(showDeleteConfirm);
                    }
                  }}
                  className="flex-1 sm:flex-none px-4 py-2 bg-rose-600 hover:bg-rose-700 text-white text-xs font-semibold rounded-lg shadow-sm shadow-rose-100 transition-all"
                >
                  Delete Resource
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

// ============================================================
// UNIT MODAL (Chapter setup overlay sheet structure form)
// ============================================================
const UnitModal = ({ isOpen, onClose, onSave, editingUnit, nextUnitNumber }) => {
  const [formData, setFormData] = useState({
    unitNumber: nextUnitNumber,
    title: '',
    description: '',
    isPublished: true
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (editingUnit) {
      setFormData({
        unitNumber: editingUnit.unitNumber || nextUnitNumber,
        title: editingUnit.title || '',
        description: editingUnit.description || '',
        isPublished: editingUnit.isPublished !== undefined ? editingUnit.isPublished : true
      });
    } else {
      setFormData({
        unitNumber: nextUnitNumber,
        title: '',
        description: '',
        isPublished: true
      });
    }
    setError(null);
  }, [editingUnit, nextUnitNumber, isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      if (editingUnit) {
        await onSave(editingUnit._id, formData);
      } else {
        await onSave(formData);
      }
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to save chapter schema settings');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center">
      <AnimatePresence>
        <motion.div
          variants={pureCssVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
          className="bg-white rounded-t-2xl sm:rounded-2xl max-w-md w-full max-h-[88vh] sm:max-h-[90vh] flex flex-col overflow-hidden border border-slate-100 shadow-xl [--modal-initial-y:100%] sm:[--modal-initial-y:15px] sm:[--modal-initial-scale:0.96] [--modal-exit-y:100%] sm:[--modal-exit-y:10px]"
        >
          <div className="w-full flex justify-center py-2 sm:hidden flex-shrink-0 bg-slate-50 border-b border-slate-100">
            <div className="w-12 h-1 bg-slate-300 rounded-full" />
          </div>

          <div className="px-5 py-4 border-b border-slate-100 bg-slate-50/50 flex items-center justify-between flex-shrink-0">
            <h3 className="text-base font-bold text-slate-800">
              {editingUnit ? 'Modify Chapter Folder Specs' : 'Configure New Lesson Chapter'}
            </h3>
            <button onClick={onClose} className="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-100 transition-colors">
              <FiX className="w-5 h-5" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="p-5 space-y-4 text-xs overflow-y-auto flex-1">
            {error && (
              <div className="p-3 bg-rose-50 border border-rose-100 rounded-xl text-rose-600 flex items-center gap-2 font-medium">
                <FiAlertCircle className="w-4 h-4 flex-shrink-0 text-rose-500" />
                {error}
              </div>
            )}

            <div className="space-y-1">
              <label className="block font-bold text-slate-500 uppercase tracking-wide">Chapter Index / Unit Number</label>
              <input
                type="number"
                min="1"
                value={formData.unitNumber}
                onChange={(e) => setFormData({ ...formData, unitNumber: parseInt(e.target.value) || 1 })}
                className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 text-slate-700 transition-all font-medium"
                required
              />
            </div>

            <div className="space-y-1">
              <label className="block font-bold text-slate-500 uppercase tracking-wide">Chapter Title Heading <span className="text-rose-500">*</span></label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 text-slate-700 transition-all font-medium"
                placeholder="e.g., Module 1: Foundational Paradigm Principles"
                required
              />
            </div>

            <div className="space-y-1">
              <label className="block font-bold text-slate-500 uppercase tracking-wide">Summary Syllabus Scope Note</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows="3"
                className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 text-slate-700 transition-all font-medium resize-none"
                placeholder="Brief list of topics contained within this textbook module unit..."
              />
            </div>

            <div className="flex items-center gap-2 pt-1">
              <input
                type="checkbox"
                id="isPublished"
                checked={formData.isPublished}
                onChange={(e) => setFormData({ ...formData, isPublished: e.target.checked })}
                className="w-4 h-4 text-indigo-600 border-slate-300 rounded focus:ring-indigo-500"
              />
              <label htmlFor="isPublished" className="text-xs font-bold text-slate-700 cursor-pointer select-none">
                Make visible to classes immediately
              </label>
            </div>

            <div className="flex justify-end gap-2 pt-4 border-t border-slate-100 flex-shrink-0">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-slate-200 text-slate-600 bg-white hover:bg-slate-50 font-bold text-xs rounded-lg transition-colors shadow-sm"
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="flex items-center gap-1.5 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-xs rounded-lg transition-all disabled:opacity-40 shadow-sm shadow-indigo-100"
              >
                <FiSave className="w-3.5 h-3.5" />
                {loading ? 'Saving Layout...' : (editingUnit ? 'Update Chapter' : 'Build Folder')}
              </button>
            </div>
          </form>
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

// ============================================================
// MATERIAL MODAL
// ============================================================
const MaterialModal = ({ isOpen, onClose, onSave, editingMaterial, unitId, units, apiBaseUrl }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    type: 'other',
    externalLink: '',
    duration: ''
  });
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [existingFiles, setExistingFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [fileAction, setFileAction] = useState('keep'); 
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (editingMaterial) {
      setFormData({
        title: editingMaterial.title || '',
        description: editingMaterial.description || '',
        type: editingMaterial.type || 'other',
        externalLink: editingMaterial.externalLink || '',
        duration: editingMaterial.duration || ''
      });
      setExistingFiles(editingMaterial.files || []);
      setSelectedFiles([]);
      setFileAction('keep');
    } else {
      setFormData({
        title: '',
        description: '',
        type: 'other',
        externalLink: '',
        duration: ''
      });
      setExistingFiles([]);
      setSelectedFiles([]);
      setFileAction('keep');
    }
    setError(null);
  }, [editingMaterial, isOpen]);

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const files = Array.from(e.target.files);
      setSelectedFiles(prev => [...prev, ...files]);
    }
    e.target.value = '';
  };

  const removeFile = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    
    try {
      if (editingMaterial) {
        let filesToSend = [];
        const updatedFormData = { ...formData };
        
        if (fileAction === 'keep') {
          filesToSend = [];
          updatedFormData._action = 'keep';
        } else if (fileAction === 'add') {
          filesToSend = selectedFiles;
          updatedFormData._action = 'addFiles';
        } else if (fileAction === 'replace') {
          filesToSend = selectedFiles;
          updatedFormData._action = 'replaceFiles';
        }
        
        await onSave(editingMaterial._id, updatedFormData, filesToSend);
      } else {
        if (selectedFiles.length === 0 && !formData.externalLink) {
          setError('Please attach at least one single file copy or type a resource URL link address');
          setLoading(false);
          return;
        }
        await onSave(unitId, formData, selectedFiles);
      }
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Submission request timed out');
      setLoading(false);
    }
  };

  const getUnitTitle = () => {
    if (!unitId) return '';
    const unit = units.find(u => u._id === unitId);
    return unit ? `Chapter ${unit.unitNumber}: ${unit.title}` : '';
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center">
      <AnimatePresence>
        <motion.div
          variants={pureCssVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
          className="bg-white rounded-t-2xl sm:rounded-2xl max-w-lg w-full max-h-[88vh] sm:max-h-[90vh] flex flex-col border border-slate-100 shadow-xl overflow-hidden [--modal-initial-y:100%] sm:[--modal-initial-y:15px] sm:[--modal-initial-scale:0.96] [--modal-exit-y:100%] sm:[--modal-exit-y:10px]"
        >
          <div className="w-full flex justify-center py-2 sm:hidden flex-shrink-0 bg-slate-50 border-b border-slate-100">
            <div className="w-12 h-1 bg-slate-300 rounded-full" />
          </div>

          <div className="px-5 py-4 border-b border-slate-100 bg-slate-50/50 flex items-center justify-between flex-shrink-0">
            <div>
              <h3 className="text-base font-bold text-slate-800">
                {editingMaterial ? 'Edit Learning Content Material' : 'Upload Study Reference Items'}
              </h3>
              {!editingMaterial && getUnitTitle() && (
                <p className="text-[11px] text-indigo-600 font-semibold mt-0.5">Target: {getUnitTitle()}</p>
              )}
              {editingMaterial && (
                <p className="text-[11px] text-slate-400 font-medium mt-0.5">Currently holds ({existingFiles.length}) resource attachments</p>
              )}
            </div>
            <button onClick={onClose} className="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-100 transition-colors">
              <FiX className="w-5 h-5" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="p-5 space-y-4 text-xs overflow-y-auto flex-1">
            {error && (
              <div className="p-3 bg-rose-50 border border-rose-100 rounded-xl text-rose-600 flex items-center gap-2 font-medium">
                <FiAlertCircle className="w-4 h-4 text-rose-500 flex-shrink-0" />
                {error}
              </div>
            )}

            <div className="space-y-1">
              <label className="block font-bold text-slate-500 uppercase tracking-wide">Material Topic Heading <span className="text-rose-500">*</span></label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 text-slate-700 transition-all font-medium"
                placeholder="e.g., Lecture Notes PDF on Vector Arrays"
                required
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1">
                <label className="block font-bold text-slate-500 uppercase tracking-wide">Resource Subtype Category</label>
                <select
                  value={formData.type}
                  onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 text-slate-700 transition-all font-medium"
                >
                  <option value="other">General Material</option>
                  <option value="pdf">Adobe PDF Sheet</option>
                  <option value="document">Word Doc Briefing</option>
                  <option value="presentation">PowerPoint Slide</option>
                  <option value="video">Class Video Stream</option>
                  <option value="audio">Voice Audio Track</option>
                  <option value="image">Diagram Illustration</option>
                  <option value="link">Web link Address</option>
                </select>
              </div>

              <div className="space-y-1">
                <label className="block font-bold text-slate-500 uppercase tracking-wide">Content Duration Length (optional)</label>
                <input
                  type="text"
                  value={formData.duration}
                  onChange={(e) => setFormData({ ...formData, duration: e.target.value })}
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 text-slate-700 transition-all font-medium"
                  placeholder="e.g., 45 Mins, 12:40"
                />
              </div>
            </div>

            <div className="space-y-1">
              <label className="block font-bold text-slate-500 uppercase tracking-wide">Annotation/Brief Notes</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows="2"
                className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 text-slate-700 transition-all font-medium resize-none"
                placeholder="Add secondary comments regarding what parts students should focus on..."
              />
            </div>

            <div className="space-y-1">
              <label className="block font-bold text-slate-500 uppercase tracking-wide">External Supplementary URL Link</label>
              <input
                type="url"
                value={formData.externalLink}
                onChange={(e) => setFormData({ ...formData, externalLink: e.target.value })}
                className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 text-slate-700 transition-all font-medium"
                placeholder="https://youtube.com/watch?v=sample"
              />
            </div>

            {editingMaterial && existingFiles.length > 0 && (
              <div className="p-3 bg-slate-50 border border-slate-200 rounded-xl space-y-1.5">
                <label className="block font-bold text-slate-400 uppercase tracking-wide">Currently Attached Files ({existingFiles.length})</label>
                <div className="space-y-1 max-h-24 overflow-y-auto no-scrollbar">
                  {existingFiles.map((file, idx) => (
                    <div key={idx} className="flex items-center justify-between bg-white px-2 py-1 rounded border border-slate-200 text-[11px] font-medium">
                      <span className="text-slate-700 truncate max-w-[180px]">{file.fileName || 'Asset sheet'}</span>
                      <span className="text-slate-400 text-[10px] ml-1 flex-shrink-0">({formatFileSize(file.fileSize)})</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {editingMaterial && (
              <div className="space-y-1.5">
                <label className="block font-bold text-slate-500 uppercase tracking-wide">Attachment Options Policy</label>
                <div className="grid grid-cols-3 gap-2">
                  {['keep', 'add', 'replace'].map((act) => (
                    <button
                      key={act}
                      type="button"
                      onClick={() => {
                        setFileAction(act);
                        setSelectedFiles([]);
                        if (act !== 'keep' && fileInputRef.current) {
                          setTimeout(() => fileInputRef.current.click(), 50);
                        }
                      }}
                      className={`py-1.5 rounded-lg font-bold border capitalize transition-all text-center ${
                        fileAction === act 
                          ? 'bg-indigo-600 border-indigo-600 text-white shadow-sm'
                          : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'
                      }`}
                    >
                      {act === 'add' ? '+ Append' : act === 'replace' ? 'Overwrite' : 'Keep Unchanged'}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {(fileAction === 'add' || fileAction === 'replace' || !editingMaterial) && (
              <div className="border-2 border-dashed border-slate-200 p-4 rounded-xl space-y-2 bg-slate-50/20 hover:border-indigo-300 transition-colors">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <span className="font-bold text-slate-700">
                    {!editingMaterial ? 'Select Document Attachments' : fileAction === 'add' ? 'Choose Additional Documents' : 'Select Overwrite Files'}
                  </span>
                  <label className="cursor-pointer">
                    <input
                      ref={fileInputRef}
                      type="file"
                      multiple
                      onChange={handleFileChange}
                      className="hidden"
                    />
                    <div className="flex items-center gap-1 px-2.5 py-1.5 bg-indigo-50 border border-indigo-100 text-indigo-600 hover:bg-indigo-100 font-bold rounded-lg transition-colors">
                      <FiUpload className="w-3 h-3" />
                      Browse Files
                    </div>
                  </label>
                </div>

                {selectedFiles.length > 0 && (
                  <div className="space-y-1 max-h-28 overflow-y-auto pr-0.5">
                    {selectedFiles.map((file, idx) => (
                      <div key={idx} className="flex items-center justify-between bg-white p-2 border border-slate-200 rounded-lg shadow-sm">
                        <span className="text-slate-700 truncate max-w-[200px] font-medium">{file.name}</span>
                        <div className="flex items-center gap-2">
                          <span className="text-[10px] text-slate-400 font-semibold font-mono">({formatFileSize(file.size)})</span>
                          <button
                            type="button"
                            onClick={() => removeFile(idx)}
                            className="w-5 h-5 flex items-center justify-center text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded"
                          >
                            <FiTrash className="w-3 h-3" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                <p className="text-[10px] text-slate-400 font-medium">Individual uploaded materials capped at 50MB maximum. All main format types allowed.</p>
              </div>
            )}

            <div className="flex justify-end gap-2 pt-4 border-t border-slate-100 flex-shrink-0">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-slate-200 text-slate-600 bg-white hover:bg-slate-50 font-bold text-xs rounded-lg transition-colors shadow-sm"
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="flex items-center gap-1.5 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-xs rounded-lg transition-all shadow-sm shadow-indigo-100"
              >
                <FiSave className="w-3.5 h-3.5" />
                {loading ? 'Uploading Data...' : (editingMaterial ? 'Update Content' : 'Publish Reference')}
              </button>
            </div>
          </form>
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

// ============================================================
// PDF CANVAS VIEWER
// Renders a PDF entirely via PDF.js onto a <canvas>, page by page, with
// prev/next navigation and zoom. Works the same way on desktop, real
// mobile browsers, and emulated mobile viewports since nothing hands off
// to a native browser PDF plugin.
// ============================================================
const PdfCanvasViewer = ({ fileUrl }) => {
  const containerRef = useRef(null);
  const canvasRef = useRef(null);
  const pdfDocRef = useRef(null);
  const renderTaskRef = useRef(null);

  const [numPages, setNumPages] = useState(0);
  const [pageNum, setPageNum] = useState(1);
  const [zoom, setZoom] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Load the document whenever the file changes
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setNumPages(0);
    setPageNum(1);
    setZoom(1);
    pdfDocRef.current = null;

    if (!fileUrl) {
      setLoading(false);
      return;
    }

    loadPdfJs()
      .then((pdfjsLib) => pdfjsLib.getDocument(fileUrl).promise)
      .then((pdf) => {
        if (cancelled) return;
        pdfDocRef.current = pdf;
        setNumPages(pdf.numPages);
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('Error loading PDF:', err);
        setError('This PDF could not be previewed here.');
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [fileUrl]);

  // Render the current page whenever page number, zoom, or container size changes
  useEffect(() => {
    if (!pdfDocRef.current || loading || error) return;
    let cancelled = false;

    const renderCurrentPage = async () => {
      try {
        const page = await pdfDocRef.current.getPage(pageNum);
        if (cancelled) return;

        const baseViewport = page.getViewport({ scale: 1 });
        const containerWidth = containerRef.current?.clientWidth || 320;
        // Fit-to-width scale, then apply the user's zoom on top of it
        const fitScale = Math.max(0.2, (containerWidth - 24) / baseViewport.width);
        const finalScale = fitScale * zoom;
        const viewport = page.getViewport({ scale: finalScale });

        const canvas = canvasRef.current;
        if (!canvas) return;
        const context = canvas.getContext('2d');

        // Handle device pixel ratio for crisp rendering on mobile screens
        const dpr = window.devicePixelRatio || 1;
        canvas.width = viewport.width * dpr;
        canvas.height = viewport.height * dpr;
        canvas.style.width = `${viewport.width}px`;
        canvas.style.height = `${viewport.height}px`;
        context.setTransform(dpr, 0, 0, dpr, 0, 0);

        if (renderTaskRef.current) {
          renderTaskRef.current.cancel();
        }
        const task = page.render({ canvasContext: context, viewport });
        renderTaskRef.current = task;
        await task.promise;
      } catch (err) {
        if (err?.name !== 'RenderingCancelledException') {
          console.error('Error rendering PDF page:', err);
        }
      }
    };

    renderCurrentPage();

    return () => {
      cancelled = true;
    };
  }, [pageNum, zoom, loading, error]);

  if (error) {
    return (
      <div className="text-center p-6 text-slate-400 space-y-3">
        <FiFileText className="w-10 h-10 mx-auto text-slate-500" />
        <p className="text-xs font-bold text-slate-200">{error}</p>
        <a
          href={fileUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-lg text-xs mx-auto"
        >
          <FiDownload className="w-3.5 h-3.5" />
          Open Original File
        </a>
      </div>
    );
  }

  return (
    <div ref={containerRef} className="w-full h-full flex flex-col bg-slate-950/40 rounded-xl overflow-hidden">
      {loading ? (
        <div className="flex-1 flex items-center justify-center text-slate-400 text-xs font-semibold gap-2">
          <div className="animate-spin rounded-full h-4 w-4 border-2 border-indigo-400 border-t-transparent" />
          Loading PDF preview...
        </div>
      ) : (
        <>
          <div className="flex-1 overflow-auto flex items-start justify-center p-3">
            <canvas ref={canvasRef} className="shadow-lg rounded bg-white" />
          </div>

          <div className="flex items-center justify-between gap-2 px-3 py-2 bg-slate-900 border-t border-slate-700 flex-shrink-0 flex-wrap">
            <div className="flex items-center gap-1.5">
              <button
                type="button"
                onClick={() => setPageNum((p) => Math.max(1, p - 1))}
                disabled={pageNum <= 1}
                className="px-2.5 py-1.5 text-[11px] font-bold text-slate-200 bg-slate-800 border border-slate-700 rounded-lg disabled:opacity-30"
              >
                Prev
              </button>
              <span className="text-[11px] font-bold text-slate-300 font-mono px-1">
                Page {pageNum} / {numPages}
              </span>
              <button
                type="button"
                onClick={() => setPageNum((p) => Math.min(numPages, p + 1))}
                disabled={pageNum >= numPages}
                className="px-2.5 py-1.5 text-[11px] font-bold text-slate-200 bg-slate-800 border border-slate-700 rounded-lg disabled:opacity-30"
              >
                Next
              </button>
            </div>

            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => setZoom((z) => Math.max(0.5, +(z - 0.2).toFixed(2)))}
                className="w-7 h-7 flex items-center justify-center text-slate-200 bg-slate-800 border border-slate-700 rounded-lg text-xs font-bold"
                title="Zoom out"
              >
                −
              </button>
              <span className="text-[10px] text-slate-400 font-mono w-10 text-center">{Math.round(zoom * 100)}%</span>
              <button
                type="button"
                onClick={() => setZoom((z) => Math.min(3, +(z + 0.2).toFixed(2)))}
                className="w-7 h-7 flex items-center justify-center text-slate-200 bg-slate-800 border border-slate-700 rounded-lg text-xs font-bold"
                title="Zoom in"
              >
                +
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

// ============================================================
// MATERIAL DETAIL MODAL
//
// PDF preview note:
// PDFs are rendered with PdfCanvasViewer (PDF.js -> <canvas>) instead of
// an <iframe src="...pdf">. An iframe pointed at a PDF hands rendering off
// to the browser's native PDF plugin (PDFium in Chrome), which behaves
// inconsistently across mobile browsers and can conflict with Chrome
// DevTools' device emulation. Rendering to canvas ourselves avoids that
// entirely and gives an actual working preview on mobile, with page
// navigation and zoom controls.
// ============================================================
const MaterialDetailModal = ({ isOpen, onClose, material, unit, apiBaseUrl, onEdit, onDelete, onAddFile, onRemoveFile }) => {
  const [activeFileIndex, setActiveFileIndex] = useState(0);
  const [isProcessingFile, setIsProcessingFile] = useState(false);
  const [showFileOptions, setShowFileOptions] = useState(null);
  
  const [showRemoveConfirmDialog, setShowRemoveConfirmDialog] = useState(false);
  const [pendingRemovalIndex, setPendingRemovalIndex] = useState(null);

  const fileInputRef = useRef(null);

  const getFileUrl = (file) => {
    if (!file) return null;
    return file.fileUrl ? `${apiBaseUrl}${file.fileUrl}` : null;
  };

  useEffect(() => {
    if (isOpen) {
      setActiveFileIndex(0);
      setShowFileOptions(null);
      setShowRemoveConfirmDialog(false);
    }
  }, [isOpen]);

  if (!isOpen || !material) return null;

  const files = material.files || [];
  const currentFile = files[activeFileIndex] || null;
  const typeConfig = getTypeBadge(material.type);
  const TypeIcon = getTypeIcon(material.type);
  const canPreview = currentFile && isPreviewable(material.type);

  const triggerFileSubmission = async (e) => {
    if (!e.target.files || e.target.files.length === 0) return;
    const targets = e.target.files[0];
    setIsProcessingFile(true);
    try {
      await onAddFile(material._id, targets);
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch (err) {
      console.error(err);
    } finally {
      setIsProcessingFile(false);
    }
  };

  const handleRemoveClickIntercept = (idx) => {
    setShowFileOptions(null);
    if (files.length <= 1) {
      return; 
    }
    setPendingRemovalIndex(idx);
    setShowRemoveConfirmDialog(true);
  };

  return (
    <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-40 flex items-end sm:items-center justify-center p-0 sm:p-4">
      <AnimatePresence>
        <motion.div
          variants={pureCssVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
          className="bg-white w-full h-[90vh] flex flex-col overflow-hidden border border-slate-200 shadow-2xl relative
            rounded-t-2xl [--modal-initial-y:100%] [--modal-exit-y:100%]
            sm:rounded-2xl sm:max-w-5xl sm:h-[82vh] sm:[--modal-initial-y:20px] sm:[--modal-initial-scale:0.96] sm:[--modal-exit-y:15px] sm:[--modal-exit-scale:0.98]"
        >
          {/* Header Controls */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 bg-slate-50 flex-shrink-0">
            <div className="flex items-center gap-3 min-w-0 flex-1">
              <div className={`w-9 h-9 rounded-lg flex items-center justify-center border flex-shrink-0 ${typeConfig.color}`}>
                <TypeIcon className="w-4 h-4" />
              </div>
              <div className="min-w-0">
                <h3 className="text-sm font-bold text-slate-800 truncate leading-snug">{material.title}</h3>
                <div className="flex items-center gap-2 text-[10px] text-slate-400 font-bold uppercase tracking-wider mt-0.5 flex-wrap">
                  <span className="text-indigo-600">{typeConfig.label}</span>
                  {unit && <span>• Unit {unit.unitNumber}: {unit.title}</span>}
                  <span>• {files.length} attachments</span>
                </div>
              </div>
            </div>
            
            <div className="flex items-center gap-1.5 flex-shrink-0 ml-4">
              <button
                onClick={onEdit}
                className="w-8 h-8 flex items-center justify-center text-blue-600 hover:bg-blue-50 border border-transparent hover:border-blue-100 rounded-lg transition-all"
                title="Edit Content"
              >
                <FiEdit2 className="w-4 h-4" />
              </button>
              <button
                onClick={onDelete}
                className="w-8 h-8 flex items-center justify-center text-rose-600 hover:bg-rose-50 border border-transparent hover:border-rose-100 rounded-lg transition-all"
                title="Remove Whole Material Card"
              >
                <FiTrash2 className="w-4 h-4" />
              </button>
              <button onClick={onClose} className="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-slate-600 hover:bg-slate-200 rounded-lg transition-colors">
                <FiX className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Split Content Box */}
          <div className="flex-1 overflow-y-auto md:overflow-hidden flex flex-col md:flex-row bg-slate-50/50">
            
            {/* Left Frame Display Element */}
            <div className="w-full md:flex-1 p-4 flex items-center justify-center relative min-h-[320px] md:min-h-0 md:h-full bg-slate-900 border-b md:border-b-0">
              {currentFile ? (
                <div className="w-full h-full flex items-center justify-center">
                  {material.externalLink ? (
                    <iframe
                      src={material.externalLink}
                      className="w-full h-full min-h-[320px] bg-white rounded-xl border border-slate-800 shadow-inner"
                      title={material.title}
                      frameBorder="0"
                      allowFullScreen
                      sandbox="allow-scripts allow-same-origin allow-popups allow-forms"
                    />
                  ) : canPreview ? (
                    <div className="w-full h-full flex items-center justify-center">
                      {material.type === 'image' && (
                        <img
                          src={getFileUrl(currentFile)}
                          alt={material.title}
                          className="max-w-full max-h-full object-contain mx-auto rounded-lg shadow-md"
                        />
                      )}
                      {material.type === 'video' && (
                        <video controls className="max-w-full max-h-full rounded-lg shadow-md" src={getFileUrl(currentFile)}>
                          Browser format codec unsupported.
                        </video>
                      )}
                      {material.type === 'pdf' && (
                        /* Renders via PDF.js -> <canvas>, so it works the same
                           way on desktop, real mobile browsers, and emulated
                           mobile viewports — no native PDF plugin involved. */
                        <PdfCanvasViewer
                          key={currentFile._id || currentFile.fileUrl || activeFileIndex}
                          fileUrl={getFileUrl(currentFile)}
                        />
                      )}
                    </div>
                  ) : (
                    <div className="text-center p-6 text-slate-400 space-y-3">
                      <FiLayers className="w-12 h-12 mx-auto text-slate-600" />
                      <div className="space-y-1">
                        <p className="text-xs font-bold text-slate-200 truncate max-w-xs">{currentFile.fileName}</p>
                        <p className="text-[11px] text-slate-500 font-mono font-bold">Size: {formatFileSize(currentFile.fileSize)}</p>
                      </div>
                      <a
                        href={getFileUrl(currentFile)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-lg shadow-md transition-all text-xs mx-auto"
                      >
                        <FiDownload className="w-3.5 h-3.5" />
                        Download File Copy
                      </a>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center p-6 text-slate-500">
                  <FiFile className="w-10 h-10 mx-auto mb-2 opacity-40" />
                  <p className="font-semibold">No active file attachments detected</p>
                </div>
              )}
            </div>

            {/* Right Attachments Sidebar */}
            <div className="w-full md:w-80 bg-white border-t md:border-t-0 md:border-l border-slate-200 p-4 flex flex-col text-xs space-y-4 md:h-full md:overflow-y-auto">
              {material.description && (
                <div className="space-y-1">
                  <h4 className="font-bold text-slate-400 uppercase tracking-wider text-[10px]">Reference Remarks</h4>
                  <p className="text-slate-600 leading-relaxed font-medium bg-slate-50 p-2.5 rounded-lg border border-slate-200/50">{material.description}</p>
                </div>
              )}

              <div className="flex-1 flex flex-col min-h-[160px]">
                <div className="flex items-center justify-between mb-2 flex-shrink-0">
                  <h4 className="font-bold text-slate-400 uppercase tracking-wider text-[10px]">Attached Resources List</h4>
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    disabled={isProcessingFile}
                    className="text-[11px] font-bold text-indigo-600 hover:text-indigo-800 flex items-center gap-0.5"
                  >
                    <FiPlus className="w-3 h-3" />
                    Upload File
                  </button>
                </div>

                <input
                  ref={fileInputRef}
                  type="file"
                  className="hidden"
                  onChange={triggerFileSubmission}
                />

                <div className="space-y-1.5 overflow-y-auto flex-1 border border-slate-100 rounded-lg p-1 bg-slate-50/40">
                  {files.map((file, idx) => (
                    <div
                      key={file._id || idx}
                      className={`p-2 rounded-lg border flex items-center justify-between cursor-pointer group/file relative ${
                        idx === activeFileIndex 
                          ? 'bg-indigo-50/80 border-indigo-200 shadow-sm'
                          : 'bg-white border-slate-200/60 hover:bg-slate-50'
                      }`}
                      onClick={() => { setActiveFileIndex(idx); setShowFileOptions(null); }}
                    >
                      <div className="flex items-center gap-2 min-w-0 flex-1">
                        <FiFile className={`w-3.5 h-3.5 flex-shrink-0 ${idx === activeFileIndex ? 'text-indigo-600' : 'text-slate-400'}`} />
                        <span className={`truncate text-xs ${idx === activeFileIndex ? 'text-indigo-900 font-bold' : 'text-slate-600 font-medium'}`}>
                          {file.fileName || `Attachment Document #${idx + 1}`}
                        </span>
                      </div>
                      <div className="flex items-center gap-1.5 flex-shrink-0 ml-2" onClick={(e) => e.stopPropagation()}>
                        <span className="text-[10px] font-semibold text-slate-400 font-mono">{formatFileSize(file.fileSize)}</span>
                        <button
                          onClick={() => setShowFileOptions(showFileOptions === idx ? null : idx)}
                          className="w-6 h-6 flex items-center justify-center text-slate-400 hover:text-slate-600 rounded-md bg-slate-100 border border-slate-200/40"
                        >
                          <FiMoreVertical className="w-3 h-3" />
                        </button>
                      </div>

                      {showFileOptions === idx && (
                        <div className="absolute right-2 top-8 bg-white border border-slate-200 shadow-xl rounded-xl p-1 z-30 w-32 animate-fadeIn">
                          <a
                            href={getFileUrl(file)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-1.5 px-2.5 py-1.5 hover:bg-slate-50 text-slate-600 rounded-lg font-semibold"
                            onClick={() => setShowFileOptions(null)}
                          >
                            <FiDownload className="w-3.5 h-3.5" />
                            Download
                          </a>
                          <button
                            type="button"
                            onClick={() => handleRemoveClickIntercept(idx)}
                            className="flex items-center gap-1.5 px-2.5 py-1.5 hover:bg-rose-50 text-rose-600 rounded-lg font-semibold w-full text-left"
                          >
                            <FiTrash className="w-3.5 h-3.5" />
                            Delete File
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Delete File Dialog Confirmation */}
          <AnimatePresence>
            {showRemoveConfirmDialog && (
              <div className="absolute inset-0 bg-slate-950/40 backdrop-blur-xs z-50 flex items-center justify-center p-4">
                <motion.div
                  initial={{ scale: 0.95, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0.95, opacity: 0 }}
                  className="bg-white border border-slate-200 rounded-xl p-4 shadow-xl max-w-xs w-full text-xs space-y-4"
                >
                  <div className="flex items-center gap-2 text-rose-600">
                    <FiAlertCircle className="w-4 h-4 flex-shrink-0" />
                    <span className="font-bold text-slate-800">Remove Attached File?</span>
                  </div>
                  {files.length <= 1 ? (
                    <p className="text-slate-500 leading-normal">
                      Cannot remove the last file asset. If you wish to clear this entry completely, please delete the main topic card instead.
                    </p>
                  ) : (
                    <p className="text-slate-500 leading-normal">
                      Are you sure you want to decouple and delete this specific document attachment from the current reference node?
                    </p>
                  )}
                  <div className="flex gap-2 justify-end">
                    <button
                      type="button"
                      onClick={() => { setShowRemoveConfirmDialog(false); setPendingRemovalIndex(null); }}
                      className="px-3 py-1.5 border border-slate-200 rounded-md font-semibold text-slate-600 bg-white hover:bg-slate-50"
                    >
                      Cancel
                    </button>
                    {files.length > 1 && (
                      <button
                        type="button"
                        onClick={async () => {
                          setShowRemoveConfirmDialog(false);
                          await onRemoveFile(material._id, pendingRemovalIndex);
                          setPendingRemovalIndex(null);
                          if (activeFileIndex >= files.length - 1) {
                            setActiveFileIndex(Math.max(0, files.length - 2));
                          }
                        }}
                        className="px-3 py-1.5 bg-rose-600 hover:bg-rose-700 text-white rounded-md font-bold"
                      >
                        Delete File
                      </button>
                    )}
                  </div>
                </motion.div>
              </div>
            )}
          </AnimatePresence>

          {/* Footer controls */}
          <div className="px-5 py-3 border-t border-slate-200 bg-slate-50 flex items-center justify-between flex-shrink-0">
            <span className="text-[10px] text-slate-400 font-medium flex items-center gap-1">
              <FiInfo className="w-3.5 h-3.5 text-slate-300" />
              Select distinct files from the sidebar to browse items.
            </span>
            <div className="flex gap-2">
              <button
                onClick={onClose}
                className="px-3.5 py-1.5 border border-slate-200 bg-white text-slate-600 font-bold rounded-lg hover:bg-slate-50"
              >
                Close
              </button>
              {currentFile && getFileUrl(currentFile) && (
                <a
                  href={getFileUrl(currentFile)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-3.5 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-lg flex items-center gap-1 shadow-sm"
                >
                  <FiMaximize2 className="w-3.5 h-3.5" />
                  Full Screen View
                </a>
              )}
            </div>
          </div>
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

export default StudyMaterialManager;